//
// chrome.ts — everything around the map
//
// Translations of `HeaderNav.swift`, `SelectedObjectPanel.swift`,
// `LegendsPanel.swift`, `PlaceSearchPanel.swift` and `HUDInfoReadout.swift`,
// rendered as plain DOM over the canvas.
//
// The visual rule, from note.md and the Swift's `GlassHUDModifiers`: **all
// backgrounds clear and transparent**, text kept legible by layered shadow
// rather than by hiding the map behind a slab. No filled panels, no frosted
// glass — a hairline border at most. Everything is monospace, tight,
// bracket-delimited, sized 8-14px. It should read as a computer terminal,
// not as a settings screen.
//

import type { AppState } from "../state";
import type { GenreId, Place, TrackedObject } from "../types";
import { GENRES, FURNITURE, labelFor } from "./arc-catalog";
import { CATEGORY_COLORS, qualifiedPlaceName } from "../types";

function el(tag: string, className?: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** A `[LABEL]` bracket button — the app's only button shape. */
function bracketButton(label: string, onClick: () => void, className = ""): HTMLElement {
  const button = el("button", `tui-button ${className}`.trim(), `[${label.toUpperCase()}]`);
  button.addEventListener("click", onClick);
  return button;
}

function telemetryRow(label: string, value: string, valueClass = ""): HTMLElement {
  const row = el("div", "telemetry-row");
  row.appendChild(el("span", "telemetry-label", label));
  row.appendChild(el("span", `telemetry-value ${valueClass}`.trim(), value));
  return row;
}

/**
 * 7500 hijack, 7600 radio failure, 7700 general emergency. These are the
 * only squawks that mean something urgent at a glance, so they get named
 * rather than left as four bare digits.
 */
const EMERGENCY_SQUAWKS: Record<string, string> = {
  "7500": "  HIJACK",
  "7600": "  RADIO FAIL",
  "7700": "  EMERGENCY",
};

/**
 * Every source AZISUR draws from, and what its licence demands. Build 1.5,
 * item 12, and a standing rule in `docs/contracts/data-sources.md`.
 *
 * **Adding a feed without adding a line here is an incomplete change.** For
 * OpenStreetMap-derived data (ODbL) and the CC BY imagery this is a licence
 * obligation, not a courtesy — and Planespotters' photographer credit is
 * already enforced at the point the photo is drawn.
 *
 * The entries marked "1.6" are not drawn yet. They are listed now, greyed,
 * so this panel is built once rather than twice.
 */
const CREDITS: Array<{ name: string; what: string; licence: string }> = [
  { name: "adsb.lol", what: "live aircraft (ADS-B)", licence: "open data" },
  { name: "CelesTrak", what: "satellite orbital elements", licence: "open data" },
  { name: "Copernicus ERA5", what: "temperature, wind, cloud, rain", licence: "Copernicus CDS" },
  { name: "Open-Meteo", what: "place search", licence: "CC BY 4.0" },
  { name: "Planespotters", what: "aircraft photographs", licence: "credited per photo" },
  { name: "NASA NeoWs", what: "near-Earth objects", licence: "public domain" },
  { name: "NASA GIBS", what: "satellite imagery", licence: "public domain · 1.6" },
  { name: "OpenStreetMap", what: "coastlines and water", licence: "ODbL · 1.6" },
  { name: "Protomaps", what: "vector map tiles", licence: "ODbL · 1.6" },
  { name: "AISstream.io", what: "live vessel positions", licence: "open data · 1.6" },
];

export class Chrome {
  private headerClock: HTMLElement;
  private headerDate: HTMLElement;
  private headerTags: HTMLElement;

  /**
   * Which genres are lit, **in the order they were switched on** — Dave:
   * *"so only the visible layers get a spot on the header, in the order
   * they were turned on."* Switch a genre off and its tag leaves; switch it
   * on again and it joins the end, not its old place. Which is why this is
   * a list that gets appended to rather than a filter over `GENRES`.
   */
  private tagOrder: GenreId[] = [];

  /** Set by main.ts so the margin row moves the arc too, not just the chrome. */
  onDockRequest?: (side: "left" | "right") => void;
  private statusPill: HTMLElement;
  private panelHost: HTMLElement;
  private legendHost: HTMLElement;
  private searchHost: HTMLElement;
  private readoutHost: HTMLElement;
  private settingsHost: HTMLElement;

  constructor(root: HTMLElement, private state: AppState) {
    // --- header ---
    //
    // **Rebuilt in build 1.5, item 9.** Dave: *"can we make one header that
    // doesn't have a line under it?… I like the AZISUR | 21:11:00 with the
    // date under it… Next to AZISUR | let's make that header dynamic for
    // whatever layer is active and visible at that time… THe time and date
    // all the way to the right."*
    //
    // One row, the full width of the window, no rule beneath it:
    //
    //   ◉ AZISUR │ AIR · SEA · SAT                          14:38:02
    //                                                  2026-08-21 UTC
    //
    const header = el("header", "hud-header");
    header.appendChild(el("span", "hud-glyph", "◉"));
    header.appendChild(el("span", "hud-title", "AZISUR"));
    header.appendChild(el("span", "hud-divider"));

    // The genre tags. Empty when nothing is showing, which after item 1 is
    // what a cold open looks like — and is correct.
    this.headerTags = el("span", "hud-tags", "");
    header.appendChild(this.headerTags);

    header.appendChild(el("span", "hud-spacer"));

    header.appendChild(
      bracketButton("AI", () => {
        this.state.isAiOpen = !this.state.isAiOpen;
        this.state.notify();
      }, "accent-violet"),
    );

    // Clock last, so it sits hard against the right edge. Fixed-size and
    // never allowed to wrap — `contracts/dial-hud.md` §9, learned when this
    // header collapsed into "GLEASO / N MAP / EXPLO…".
    const clockBlock = el("div", "hud-clock-block");
    this.headerClock = el("span", "hud-clock", "--:--:--");
    this.headerDate = el("span", "hud-date", "");
    clockBlock.appendChild(this.headerClock);
    clockBlock.appendChild(this.headerDate);
    header.appendChild(clockBlock);

    root.appendChild(header);

    // --- status row ---
    const statusRow = el("div", "hud-status-row");
    this.statusPill = el("span", "tui-bracket status-ok", "[BOOTING]");
    statusRow.appendChild(this.statusPill);
    statusRow.appendChild(
      bracketButton("Find", () => this.state.togglePlaceSearch(), "accent-mint"),
    );
    statusRow.appendChild(
      bracketButton("Legend", () => {
        this.state.isLegendOpen = !this.state.isLegendOpen;
        this.state.notify();
      }),
    );
    statusRow.appendChild(
      bracketButton("+", () => this.state.zoomIn()),
    );
    statusRow.appendChild(
      bracketButton("−", () => this.state.zoomOut()),
    );
    statusRow.appendChild(
      bracketButton("◎", () => this.state.resetZoom()),
    );
    root.appendChild(statusRow);

    this.searchHost = el("div", "hud-search-host");
    root.appendChild(this.searchHost);

    this.legendHost = el("div", "hud-legend-host");
    root.appendChild(this.legendHost);

    this.panelHost = el("div", "hud-panel-host");
    root.appendChild(this.panelHost);

    this.readoutHost = el("div", "hud-readout");
    root.appendChild(this.readoutHost);

    this.settingsHost = el("div", "hud-settings-host");
    root.appendChild(this.settingsHost);

    state.subscribe(() => this.render());
    this.render();
  }

  render(): void {
    this.renderHeader();
    this.renderStatus();
    this.renderSearch();
    this.renderLegend();
    this.renderPanel();
    this.renderReadout();
    this.renderSettings();
  }

  /**
   * Keeps `tagOrder` in step with what is actually on the map: a genre
   * joins the end when its first layer lights, and drops out when its last
   * one goes dark.
   */
  private syncHeaderTags(): void {
    const lit = new Set(
      GENRES.filter((g) => g.seats.some((s) => this.state.layers.on[s.key])).map((g) => g.id),
    );
    this.tagOrder = this.tagOrder.filter((id) => lit.has(id));
    for (const g of GENRES) {
      if (lit.has(g.id) && !this.tagOrder.includes(g.id)) this.tagOrder.push(g.id);
    }
    const tags = this.tagOrder.map((id) => GENRES.find((g) => g.id === id)!.tag);
    this.headerTags.textContent = tags.length ? tags.join(" · ") : "";
  }

  // --- header -----------------------------------------------------------

  private renderHeader(): void {
    const [date, time] = this.state.utcTimeString.split(" ");
    this.headerClock.textContent = time ?? "--:--:--";
    this.headerDate.textContent = date ? `${date} UTC` : "";
    this.syncHeaderTags();
  }

  private renderStatus(): void {
    if (this.state.isRefreshing) {
      this.statusPill.textContent = "[SYNCING FEEDS]";
      this.statusPill.className = "tui-bracket status-busy";
    } else {
      this.statusPill.textContent = `[${this.state.objects.length} CONTACTS]`;
      this.statusPill.className = "tui-bracket status-ok";
    }
  }

  // --- selected object --------------------------------------------------

  private renderPanel(): void {
    this.panelHost.replaceChildren();
    const obj = this.state.selectedObject;
    if (!obj) return;

    const isMilitary = obj.category === "military_aircraft";
    const isAircraft = obj.category === "flight" || isMilitary;
    const isVessel = obj.category === "vessel";
    const isPlace = obj.category === "place";
    const accent = CATEGORY_COLORS[obj.category];

    const panel = el("section", "hud-panel");
    panel.style.setProperty("--accent", accent);

    const head = el("div", "panel-head");
    head.appendChild(el("span", "panel-eyebrow", "SELECTED TELEMETRY"));
    head.appendChild(bracketButton("X", () => this.state.selectObject(null), "dim"));
    panel.appendChild(head);

    const title = el("div", "panel-title");
    title.appendChild(el("span", "panel-dot"));
    title.appendChild(el("span", "panel-name", obj.name));
    panel.appendChild(title);
    panel.appendChild(el("div", "panel-type", obj.typeLabel || "Tracked Surface/Orbital Entity"));
    panel.appendChild(
      el("div", "tui-bracket panel-category", `[${obj.category.replace(/_/g, " ").toUpperCase()}]`),
    );

    // Photograph of this exact airframe. Attribution is not optional — it
    // is the condition Planespotters attaches to free use of the API.
    if (obj.photoUrl) {
      const figure = el("figure", "panel-photo");
      const img = document.createElement("img");
      img.src = obj.photoUrl;
      img.alt = `Photograph of ${obj.registration ?? obj.name}`;
      img.loading = "lazy";
      figure.appendChild(img);
      if (obj.photoCredit) {
        const caption = el("figcaption");
        const link = document.createElement("a");
        link.href = obj.photoPageUrl ?? "https://www.planespotters.net";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.textContent = `PHOTO © ${obj.photoCredit.toUpperCase()} / PLANESPOTTERS.NET`;
        caption.appendChild(link);
        figure.appendChild(caption);
      }
      panel.appendChild(figure);
    }

    const rows = el("div", "panel-rows");
    if (isAircraft) {
      rows.appendChild(telemetryRow("Callsign", obj.flightNumber ?? "—", "accent-sky"));
      rows.appendChild(telemetryRow("Registration", obj.registration ?? "—"));
      rows.appendChild(telemetryRow("Aircraft Type", obj.aircraftTypeCode ?? "—"));
      rows.appendChild(telemetryRow("ICAO 24-bit", obj.icao24?.toUpperCase() ?? "—"));
      rows.appendChild(telemetryRow("Operator", obj.airline ?? "Unidentified"));
      if (obj.origin) rows.appendChild(telemetryRow("Origin", obj.origin));
      if (obj.destination) rows.appendChild(telemetryRow("Destination", obj.destination));
      rows.appendChild(telemetryRow("Altitude", obj.altitudeFt ? `${Math.round(obj.altitudeFt)} ft` : "—"));
      rows.appendChild(telemetryRow("Ground Speed", obj.speedKnots ? `${Math.round(obj.speedKnots)} knots` : "—"));
      rows.appendChild(telemetryRow("Track", obj.headingDeg !== undefined ? `${Math.round(obj.headingDeg)}°` : "—"));
      if (obj.verticalRateFpm !== undefined && Math.abs(obj.verticalRateFpm) > 32) {
        const rate = obj.verticalRateFpm;
        rows.appendChild(telemetryRow(
          "Vertical Rate",
          `${rate > 0 ? "+" : ""}${Math.round(rate)} ft/min`,
          rate > 0 ? "accent-mint" : "accent-amber",
        ));
      }
      if (obj.squawk) {
        const note = EMERGENCY_SQUAWKS[obj.squawk] ?? "";
        rows.appendChild(telemetryRow("Squawk", obj.squawk + note, note ? "accent-red" : ""));
      }
    } else if (isPlace) {
      rows.appendChild(telemetryRow("Classification", obj.typeLabel));
      rows.appendChild(telemetryRow("Timezone", obj.lastUpdated));
    } else if (isVessel) {
      rows.appendChild(telemetryRow("MMSI ID", obj.mmsi ?? "—", "accent-mint"));
      rows.appendChild(telemetryRow("Vessel Class", obj.vesselType ?? "—"));
      rows.appendChild(telemetryRow("Port of Departure", obj.origin ?? "—"));
      rows.appendChild(telemetryRow("Destination Port", obj.destination ?? "—"));
      rows.appendChild(telemetryRow("Cruising Speed", obj.speedKnots ? `${Math.round(obj.speedKnots)} knots` : "—"));
      rows.appendChild(telemetryRow("Course / Heading", obj.headingDeg !== undefined ? `${Math.round(obj.headingDeg)}°` : "—"));
    } else {
      rows.appendChild(telemetryRow("Altitude", obj.altitudeKm ? `${obj.altitudeKm.toFixed(1)} km` : "N/A"));
      rows.appendChild(telemetryRow("Velocity", obj.velocityKms ? `${obj.velocityKms.toFixed(2)} km/s` : "N/A"));
      if (obj.orbitalPeriodMin) rows.appendChild(telemetryRow("Orbital Period", `${obj.orbitalPeriodMin.toFixed(1)} min`));
      if (obj.inclinationDeg) rows.appendChild(telemetryRow("Inclination", `${obj.inclinationDeg.toFixed(1)}°`));
      if (obj.elements) rows.appendChild(telemetryRow("NORAD ID", String(obj.elements.noradCatId)));
    }

    rows.appendChild(telemetryRow(
      "Latitude",
      `${Math.abs(obj.latitude).toFixed(2)}° ${obj.latitude >= 0 ? "N" : "S"}`,
    ));
    rows.appendChild(telemetryRow(
      "Longitude",
      `${Math.abs(obj.longitude).toFixed(2)}° ${obj.longitude >= 0 ? "E" : "W"}`,
    ));
    rows.appendChild(telemetryRow("Data Feed", obj.dataSource));
    rows.appendChild(telemetryRow("Last Telemetry", obj.lastUpdated));
    panel.appendChild(rows);

    const actions = el("div", "panel-actions");
    actions.appendChild(bracketButton("Center On Projection", () => this.state.centerOnObject(obj)));
    panel.appendChild(actions);

    this.panelHost.appendChild(panel);
  }

  // --- legend -----------------------------------------------------------

  private renderLegend(): void {
    this.legendHost.replaceChildren();
    if (!this.state.isLegendOpen) return;

    const rows: Array<[TrackedObject["category"], string]> = [
      ["flight", "Live Aircraft (ADS-B)"],
      ["military_aircraft", "Military / State Aircraft"],
      ["vessel", "Maritime Vessels (fixed sample)"],
      ["active", "Active Satellites (ISS/GPS/GOES)"],
      ["starlink", "Starlink Constellation"],
      ["neo_safe", "NASA NEO Asteroids"],
      ["subsolar", "Sub-Solar / Sub-Lunar Points"],
      ["place", "Pinned Places (Search)"],
    ];

    const legend = el("section", "hud-legend");
    legend.appendChild(el("div", "panel-eyebrow", "TRACKING LEGEND"));
    for (const [category, label] of rows) {
      const row = el("div", "legend-row");
      const dot = el("span", "legend-dot");
      dot.style.setProperty("--dot", CATEGORY_COLORS[category]);
      row.appendChild(dot);
      row.appendChild(el("span", "legend-label", label));
      legend.appendChild(row);
    }
    this.legendHost.appendChild(legend);
  }

  // --- place search -----------------------------------------------------

  private searchDebounce: number | null = null;

  private renderSearch(): void {
    this.searchHost.replaceChildren();
    if (!this.state.isPlaceSearchOpen) return;

    const panel = el("section", "hud-search");
    const head = el("div", "panel-head");
    head.appendChild(el("span", "panel-eyebrow", "PLACE SEARCH"));
    head.appendChild(bracketButton("X", () => this.state.togglePlaceSearch(), "dim"));
    panel.appendChild(head);

    const inputRow = el("div", "search-input-row");
    inputRow.appendChild(el("span", "search-prompt", ">"));
    const input = document.createElement("input");
    input.type = "search";
    input.className = "search-input";
    input.value = this.state.placeSearchQuery;
    input.placeholder = "city, airport, anywhere";
    input.autocomplete = "off";
    input.addEventListener("input", () => {
      this.state.placeSearchQuery = input.value;
      // Debounced by hand: searching on every keystroke would hammer the
      // geocoder, so the query only goes out once typing pauses.
      if (this.searchDebounce !== null) window.clearTimeout(this.searchDebounce);
      this.searchDebounce = window.setTimeout(() => void this.state.runPlaceSearch(), 350);
    });
    inputRow.appendChild(input);
    panel.appendChild(inputRow);

    if (this.state.isSearchingPlaces) {
      panel.appendChild(el("div", "tui-bracket dim", "[SEARCHING…]"));
    }

    if (this.state.placeSearchResults.length > 0) {
      const list = el("div", "search-results");
      for (const place of this.state.placeSearchResults) {
        const row = el("button", "search-result");
        row.appendChild(el("span", "tree-branch", "├─"));
        const body = el("span", "search-result-body");
        body.appendChild(el("span", "search-result-name", qualifiedPlaceName(place)));
        body.appendChild(el("span", "search-result-coords", this.coordinateLine(place)));
        row.appendChild(body);
        row.addEventListener("click", () => this.state.pinPlace(place));
        list.appendChild(row);
      }
      panel.appendChild(list);
    }

    if (this.state.pinnedPlaces.length > 0) {
      const pinned = el("div", "search-pinned");
      pinned.appendChild(el("div", "panel-eyebrow", `PINNED (${this.state.pinnedPlaces.length})`));
      for (const place of this.state.pinnedPlaces) {
        const row = el("div", "pinned-row");
        const dot = el("span", "legend-dot");
        dot.style.setProperty("--dot", CATEGORY_COLORS.place);
        row.appendChild(dot);
        row.appendChild(el("span", "pinned-name", place.name));
        row.appendChild(bracketButton("Unpin", () => this.state.unpinPlace(place), "dim tiny"));
        pinned.appendChild(row);
      }
      panel.appendChild(pinned);
    }

    this.searchHost.appendChild(panel);
    // Focus after insertion, so opening the panel is one action not two.
    input.focus();
  }

  private coordinateLine(place: Place): string {
    const latDir = place.latitude >= 0 ? "N" : "S";
    const lonDir = place.longitude >= 0 ? "E" : "W";
    let line =
      `${Math.abs(place.latitude).toFixed(2)}°${latDir} ` +
      `${Math.abs(place.longitude).toFixed(2)}°${lonDir}`;
    if (place.population && place.population > 0) {
      line += `  ·  pop ${place.population.toLocaleString("en-US")}`;
    }
    return line;
  }

  // --- transient readout ------------------------------------------------

  private renderReadout(): void {
    this.readoutHost.textContent = this.state.hudFeedbackMessage ?? "";
    this.readoutHost.classList.toggle("visible", this.state.hudFeedbackMessage !== null);
  }

  // --- dial settings ----------------------------------------------------

  private renderSettings(): void {
    this.settingsHost.replaceChildren();
    if (!this.state.isSettingsOpen) return;

    const scrim = el("div", "settings-scrim");
    scrim.addEventListener("click", () => {
      this.state.isSettingsOpen = false;
      this.state.notify();
    });

    const sheet = el("div", "settings-sheet");
    sheet.addEventListener("click", (e) => e.stopPropagation());

    const head = el("div", "panel-head");
    head.appendChild(el("span", "panel-eyebrow", "DIAL SETTINGS"));
    head.appendChild(bracketButton("Close", () => {
      this.state.isSettingsOpen = false;
      this.state.notify();
    }, "dim"));
    sheet.appendChild(head);

    // --- margin side. Item 10: one setting moves the chrome and the menu.
    const dockRow = el("div", "settings-row");
    dockRow.appendChild(el("span", "settings-label", "Margin side"));
    for (const side of ["left", "right"] as const) {
      const active = this.state.dockSide === side;
      dockRow.appendChild(
        bracketButton(active ? `${side} ●` : side, () => {
          this.onDockRequest?.(side);
        }, active ? "" : "dim"),
      );
    }
    sheet.appendChild(dockRow);

    // --- map furniture. Item 12: the four layers that are not contacts.
    sheet.appendChild(el("div", "settings-eyebrow", "MAP FURNITURE"));
    for (const f of FURNITURE) {
      const row = el("div", "settings-row");
      row.appendChild(el("span", "settings-label", f.label));
      const on = this.state.layers.on[f.key];
      row.appendChild(
        bracketButton(on ? "on ●" : "off", () => {
          this.state.layers.on[f.key] = !this.state.layers.on[f.key];
          this.state.showHUDFeedback(
            `[${labelFor(f.key).toUpperCase()}] — ${this.state.layers.on[f.key] ? "ON" : "OFF"}`,
          );
          this.state.notify();
        }, on ? "" : "dim"),
      );
      sheet.appendChild(row);
    }

    // --- credits. Item 12, and a standing rule in
    // `docs/contracts/data-sources.md`: every feed the app draws from
    // appears here, with its licence. Two of these are obligations rather
    // than courtesies — ODbL and CC BY both require attribution — so a feed
    // added without a line here is an incomplete change.
    sheet.appendChild(el("div", "settings-eyebrow", "CREDITS"));
    for (const c of CREDITS) {
      const row = el("div", "credit-row");
      row.appendChild(el("span", "credit-name", c.name));
      row.appendChild(el("span", "credit-what", c.what));
      row.appendChild(el("span", "credit-licence", c.licence));
      sheet.appendChild(row);
    }

    const about = el("div", "settings-about");
    about.textContent =
      "AZISUR — Gleason azimuthal equidistant projection instrument, " +
      "centred on the North Pole.";
    sheet.appendChild(about);

    scrim.appendChild(sheet);
    this.settingsHost.appendChild(scrim);
  }
}
